import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import { Delaunay } from "d3-delaunay";
import concaveman from "concaveman";
import { polygonContains } from "d3-polygon";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { getEfficiencyMatrix } from "../api/index";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import Loader from "../assets/Loader.png";
import LoaderAnimation from "../assets/LoaderAnimation.png";
import "../App.css";

const EfficiencyMatrixPlot = ({
  width = 1200,
  height = 520,
  selectedMotors = [],
  onlyRender = false,
  measurementUnit,
}) => {
  const svgRef = useRef();
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [efficiencyData, setEfficiencyData] = useState([]);
  const [clickMarker, setClickMarker] = useState(null);
  const [downloadExcel, setdownloadExcel] = useState([]);
  const [plotWidth, setPlotWidth] = useState(880);
  const [loader, setLoader] = useState(true);
  const [hoverMarker, setHoverMarker] = useState(null);
  const [hover, setHover] = useState(false);
  const colorPalette = ["#869098", "#d71920", "#000000"];

  // useEffect(() => {
  //   const handleResize = () => setPlotWidth(window.innerWidth);
  //   window.addEventListener("resize", handleResize);
  //   return () => window.removeEventListener("resize", handleResize);
  // }, []);

  useEffect(() => {
    setLoader(true);
    setEfficiencyData([]);
    setdownloadExcel([]);
    setSelectedPoints([]);
    setClickMarker(null);
  }, [measurementUnit]);

  useEffect(() => {
    if (!selectedMotors.length) {
      setEfficiencyData([]);
      setdownloadExcel([]);
      setSelectedPoints([]);
      setClickMarker(null);
      return;
    }

    setLoader(true);
    setSelectedPoints([]); // only clear selection, keep old diagram
    setClickMarker(null);

    async function fetchData() {
      try {
        const responses = await Promise.all(
          selectedMotors.map((motor) =>
            getEfficiencyMatrix({
              series: motor.series,
              displacement: motor.displacement,
              viscosity: motor.viscosity || 0,
              measurementUnit: measurementUnit,
            })
          )
        );

        // Merge new data with old data to prevent "component disappearing"
        const newEfficiencyData = responses.map((res) => res.data);
        setEfficiencyData(newEfficiencyData);
        setdownloadExcel(newEfficiencyData);
      } catch (error) {
        console.error("Error fetching efficiency data:", error);
      } finally {
        setLoader(false);
      }
    }

    fetchData();
  }, [selectedMotors, measurementUnit]);

  function drawLabelWithBox(g, x, y, textContent, anchor = "middle") {
    const labelGroup = g.append("g");

    const text = labelGroup
      .append("text")
      .text(textContent)
      .attr("x", x)
      .attr("y", y)
      .attr("dy", "0.35em")
      .attr("text-anchor", anchor)
      .style("font-size", "12px")

      .style("fill", "white")
      .style(
        "font-family",
        `"Myriad Pro", "Source Sans Pro", "system-ui", "sans-serif"`
      );

    const bbox = text.node().getBBox();

    labelGroup
      .insert("rect", "text")
      .attr("x", bbox.x - 4)
      .attr("y", bbox.y - 2)
      .attr("width", bbox.width + 8)
      .attr("height", bbox.height + 4)
      .attr("fill", "black")
      .attr("stroke", "white")
      .attr("rx", 0)
      .attr("ry", 0);

    return labelGroup;
  }

  useEffect(() => {
    d3.selection.prototype.styles = function (styles) {
      for (const [key, value] of Object.entries(styles)) {
        this.style(key, value);
      }
      return this;
    };
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    if (!efficiencyData.length || selectedMotors.length === 0) {
      return;
    }

    const margin = { top: 20, right: 30, bottom: 40, left: 70 };
    const innerWidth = plotWidth - 150 - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    const clickGroup = g.append("g").attr("class", "click-labels");

    const allPoints = efficiencyData.flatMap((motor) =>
      (motor.efficiency_point || []).map((pt) => ({
        speed: pt.speed,
        torque: pt.torque,
        flow: pt.flow,
        pressure: pt.pressure,
        efficiency: pt.overall_efficiency,
        continuous: pt.is_continuous_operation,
        motor: `${motor.series} ${motor.displacement}`,
      }))
    );

    if (!allPoints.length) return;

    const xScale = d3
      .scaleLinear()
      .domain(d3.extent(allPoints, (d) => d.speed))
      .range([0, innerWidth]);

    const maxTorque = d3.max(allPoints, (d) => d.torque);

    const yScale = d3
      .scaleLinear()
      .domain([0, maxTorque]) // ✅ force start from zero
      .range([innerHeight, 0]);


    // const h = allPoints.map((pt) => [xScale(pt.speed), yScale(pt.torque)]);
    // const hull = concaveman(h, 15, 0);
    // g.append("path")
    //   .attr("d", d3.line()(hull)) // close the path
    //   .attr("fill", "Grey")
    //   .attr("opacity", 0.15)
    //   .attr("stroke", "none");

    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale).tickFormat(d3.format("d")));
    g.append("g").call(d3.axisLeft(yScale).tickFormat(d3.format("d")));

    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("x", margin.left + innerWidth / 2)
      .attr("y", height - 6)
      .style("font-size", "14px")
      .style(
        "font-family",
        `"Myriad Pro", "Source Sans Pro", "system-ui", "sans-serif"`
      )
      .text("Speed [RPM]");

    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr(
        "transform",
        `translate(${margin.left - 50}, ${
          margin.top + innerHeight / 2
        }) rotate(-90)`
      )
      .style("font-size", "14px")
      .style(
        "font-family",
        `"Myriad Pro", "Source Sans Pro", "system-ui", "sans-serif"`
      )
      .text(measurementUnit === "Imperial" ? "Torque [lbf-in]" : "Torque [Nm]");

    efficiencyData.forEach((motor, motorIndex) => {
      const color = colorPalette[motorIndex % colorPalette.length];
      const points = motor.efficiency_point || [];

      const groupedByPressure = Array.from(
        d3.group(points, (d) => d.pressure)
      ).sort((a, b) => a[0] - b[0]);

      groupedByPressure.forEach(([pressure, pts]) => {
        const sortedPts = pts.slice().sort((a, b) => a.flow - b.flow);
        const line = d3
          .line()
          .x((d) => xScale(d.speed))
          .y((d) => yScale(d.torque));
        g.append("path")
          .datum(sortedPts)
          .attr("fill", "none")
          .attr("stroke", color)
          .attr("stroke-width", 0.8)
          .attr("d", line);
      });

      const groupedByFlow = Array.from(d3.group(points, (d) => d.flow)).sort(
        (a, b) => a[0] - b[0]
      );

      groupedByFlow.forEach(([flow, pts]) => {
        const sortedPts = pts.slice().sort((a, b) => a.pressure - b.pressure);
        const line = d3
          .line()
          .x((d) => xScale(d.speed))
          .y((d) => yScale(d.torque));
        g.append("path")
          .datum(sortedPts)
          .attr("fill", "none")
          .attr("stroke", color)
          .attr("stroke-width", 0.8)
          .attr("d", line);
      });

      const continuousPoints = points.filter(
        (pt) => pt.is_continuous_operation === true
      );
      if (continuousPoints.length >= 3) {
        const coords = continuousPoints.map((pt) => [
          xScale(pt.speed),
          yScale(pt.torque),
        ]);
        const hull = concaveman(coords, 25, 0);
        g.append("path")
          .attr("d", d3.line()(hull)) // close the path
          .attr("fill", color)
          .attr("opacity", 0.15)
          .attr("stroke", "none");
      }
    });

    const delaunay = Delaunay.from(
      allPoints,
      (d) => xScale(d.speed),
      (d) => yScale(d.torque)
    );

    svg.on("click", function (event) {
      const [mx, my] = d3.pointer(event);
      const dx = mx - margin.left;
      const dy = my - margin.top;

      const x = xScale.invert(dx);
      const y = yScale.invert(dy);
      const xDomain = xScale.domain();
      const yDomain = yScale.domain();

      if (
        dx < 0 ||
        dx > innerWidth ||
        dy < 0 ||
        dy > innerHeight ||
        x < xDomain[0] ||
        x > xDomain[1] ||
        y < yDomain[0] ||
        y > yDomain[1]
      ) {
        setSelectedPoints([]);
        return;
      }
      setClickMarker({ x: dx, y: dy });
      clickGroup.selectAll("*").remove();
      drawLabelWithBox(clickGroup, dx, height + 20, `${x.toFixed(1)} rpm`);
      drawLabelWithBox(clickGroup, -35, dy, `${y.toFixed(1)} Nm`, "end");

      const filtered = efficiencyData.flatMap((motor) => {
        const pts = (motor.efficiency_point || []).map((pt) => [
          pt.speed,
          pt.torque,
          pt.flow,
          pt.pressure,
          pt.overall_efficiency,
          pt.is_continuous_operation ? true : false,
        ]);
        if (pts.length < 3) return [];

        const hull = concaveman(
          pts.map((d) => [d[0], d[1]]),
          15,
          0
        );
        const isInside = polygonContains(hull, [x, y]);
        if (!isInside) return [];
        const continuousPts = pts.filter((d) => d[5] === true);
        let insideContinuous = false;
        if (continuousPts.length >= 3) {
          const continuousHull = concaveman(
            continuousPts.map((d) => [d[0], d[1]]),
            25,
            0
          );
          insideContinuous = polygonContains(continuousHull, [x, y]);
        }

        const idw = (pts, x, y, idx, k = 4, p = 2) => {
          if (!pts.length) return NaN;
          const ns = pts
            .map((p) => ({ d2: (p[0] - x) ** 2 + (p[1] - y) ** 2, v: p[idx] }))
            .sort((a, b) => a.d2 - b.d2)
            .slice(0, Math.min(k, pts.length));
          if (ns[0].d2 === 0) return ns[0].v;
          const w = (d2) => 1 / d2 ** (p / 2);
          return (
            ns.reduce((sum, n) => sum + n.v * w(n.d2), 0) /
            ns.reduce((sum, n) => sum + w(n.d2), 0)
          );
        };

        return [
          {
            series: motor.series,
            displacement: motor.displacement,
            torque: y,
            speed: x,
            pressure: idw(pts, x, y, 3).toFixed(0),
            flow: idw(pts, x, y, 2).toFixed(1),
            overall_efficiency: (idw(pts, x, y, 4) * 100).toFixed(2),
            continuous_operation: insideContinuous, // ✅ set based on shaded region
          },
        ];
      });
      setSelectedPoints(filtered);
    });

    svg.on("mousemove", function (event) {
      const [mx, my] = d3.pointer(event);
      const dx = mx - margin.left;
      const dy = my - margin.top;
      const x = xScale.invert(dx);
      const y = yScale.invert(dy);

      // Check if (x, y) is within any motor's hull
      let insideAnyHull = false;

      for (const motor of efficiencyData) {
        const pts = (motor.efficiency_point || []).map((pt) => [
          pt.speed,
          pt.torque,
        ]);
        if (pts.length < 3) continue;

        const hull = concaveman(pts, 15, 0);
        if (polygonContains(hull, [x, y])) {
          insideAnyHull = true;
          break;
        }
      }

      if (!insideAnyHull) {
        setHoverMarker(null);
        return;
      }

      setHoverMarker({ x: dx, y: dy, dataX: x, dataY: y });
    });
    svg.on("mouseleave", () => setHoverMarker(null));

    if (clickMarker) {
      const markerGroup = g.append("g").attr("class", "click-marker");

      markerGroup
        .append("circle")
        .attr("cx", clickMarker.x)
        .attr("cy", clickMarker.y)
        .attr("r", 6)
        .attr("fill", "white")
        .attr("stroke", "black")
        .attr("stroke-width", 2);

      markerGroup
        .append("line")
        .attr("x1", 0)
        .attr("y1", clickMarker.y)
        .attr("x2", innerWidth)
        .attr("y2", clickMarker.y)
        .attr("stroke", "black")
        .attr("stroke-width", 1);

      markerGroup
        .append("line")
        .attr("x1", clickMarker.x)
        .attr("y1", 0)
        .attr("x2", clickMarker.x)
        .attr("y2", innerHeight)
        .attr("stroke", "black")
        .attr("stroke-width", 1);

      drawLabelWithBox(
        markerGroup,
        clickMarker.x,
        innerHeight + 20,
        `${Math.round(xScale.invert(clickMarker.x))}`,
        "middle"
      );
      drawLabelWithBox(
        markerGroup,
        -10,
        clickMarker.y,
        `${Math.round(yScale.invert(clickMarker.y))}`,
        "end"
      );

      markerGroup.raise();
    }

    if (hoverMarker) {
      const hoverGroup = g.append("g").attr("class", "hover-marker");

      hoverGroup
        .append("line")
        .attr("x1", hoverMarker.x)
        .attr("y1", 0)
        .attr("x2", hoverMarker.x)
        .attr("y2", innerHeight)
        .attr("stroke", "rgb(215,25,32)")
        .attr("stroke-width", 1);

      hoverGroup
        .append("line")
        .attr("x1", 0)
        .attr("y1", hoverMarker.y)
        .attr("x2", innerWidth)
        .attr("y2", hoverMarker.y)
        .attr("stroke", "rgb(215,25,32)")
        .attr("stroke-width", 1);

      const axisFont = {
        "font-size": "12px",
        "font-family": `"Myriad Pro", "Source Sans Pro", "system-ui", "sans-serif"`,
        fill: "white",
      };

      const drawHoverLabel = (x, y, text, anchor = "middle") => {
        const labelGroup = g.append("g");

        const txt = labelGroup
          .append("text")
          .text(text)
          .attr("x", x)
          .attr("y", y)
          .attr("dy", "0.35em")
          .attr("text-anchor", anchor)
          .styles(axisFont);

        const bbox = txt.node().getBBox();

        labelGroup
          .insert("rect", "text")
          .attr("x", bbox.x - 4)
          .attr("y", bbox.y - 2)
          .attr("width", bbox.width + 8)
          .attr("height", bbox.height + 4)
          .attr("fill", "rgb(215,25,32)")
          .attr("rx", 0)
          .attr("ry", 0);
      };

      drawHoverLabel(
        hoverMarker.x,
        innerHeight + 20,
        `${Math.round(hoverMarker.dataX)}`,
        "middle"
      );
      drawHoverLabel(
        -10,
        hoverMarker.y,
        `${Math.round(hoverMarker.dataY)}`,
        "end"
      );
    }
  }, [
    efficiencyData,
    plotWidth,
    height,
    clickMarker,
    hoverMarker,
    selectedMotors,
  ]);

  const downloadFromDownloadExcel = () => {
    if (!downloadExcel.length) {
      alert("No data available for download.");
      return;
    }

    const wb = XLSX.utils.book_new();
    const usedNames = new Set();

    downloadExcel.forEach((motor, idx) => {
      if (!motor.efficiency_point || motor.efficiency_point.length === 0)
        return;

      let baseName = `${motor.series}_${motor.displacement}`.replace(
        /[:\\\/\?\*\[\]]/g,
        "_"
      );

      let sheetName = baseName.substring(0, 31);
      let suffix = 1;
      while (usedNames.has(sheetName)) {
        const suffixStr = `_${suffix++}`;
        const truncatedBase = baseName.substring(0, 31 - suffixStr.length);
        sheetName = truncatedBase + suffixStr;
      }
      usedNames.add(sheetName);

      const data = motor.efficiency_point.map((point) => ({
        Speed: point.speed,
        Torque: point.torque,
        Pressure: point.pressure,
        Flow: point.flow,
        "Overall Efficiency": point.overall_efficiency,
        "Continuous Operation": point.is_continuous_operation
          ? "True"
          : "False",
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    XLSX.writeFile(wb, "EfficiencyMatrix_Selected.xlsx");
  };

  const filteredSelectedMotors = selectedMotors.filter((motor) =>
    efficiencyData.some(
      (data) =>
        data.series === motor.series && data.displacement === motor.displacement
    )
  );

  return (
    <div style={{ marginTop: "-20px" }}>
      <div>
        {filteredSelectedMotors.length === 0 ? (
          <div
            style={{
              justifyContent: "center",
              display: "flex",
              alignItems: "center",
              height: "20px",
              width: "1400px",
              flexShrink: 0,
            }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              style={{
                padding: "8px",
                fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
              }}
            >
              No motors selected. Use the table checkboxes to display efficiency
              matrix.
            </Typography>
          </div>
        ) : loader ? (
          <div
            className="loader-container"
            style={{
              width: "1400px",
              flexShrink: 0,
              // backgroundColor: "red",
              height: "500px",
            }}
          >
            <div className="dot-loader">
              <div class="dot"></div>
              <div class="dot"></div>
              <div class="dot"></div>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ position: "relative" }}>
              <Typography
                variant="h6"
                style={{
                  fontWeight: "bold",
                  marginBottom: "10px",
                  marginLeft: "20px", // match left margin of plot
                  fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                }}
              >
                Full Function Diagram:
              </Typography>
              {/* <svg ref={svgRef} width={plotWidth} height={height} /> */}
            </div>
            {/* // addd */}
            <div
              style={{
                display: "flex",
                flexDirection: "row", // horizontal layout
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: "80px", // optional spacing between plot and table
              }}
            >
              <div
                style={{
                  width: "700px",
                  flexShrink: 0,
                  // backgroundColor: "red",
                  height: "500px",
                }}
              >
                <svg ref={svgRef} width={plotWidth} height={height} />
              </div>
              <div style={{ flex: 1, minWidth: "700px" }}>
                {/* Selected Motors List */}
                <div style={{ marginBottom: "20px" }}>
                  <h4
                    style={{
                      fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                    }}
                  >
                    Selected Motors :
                  </h4>
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: 0,
                      display: "flex",
                      gap: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    {selectedMotors.map((motor, i) => (
                      <li
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                        }}
                      >
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            backgroundColor: colorPalette[i % 10],
                            borderRadius: "50%",
                            display: "inline-block",
                            marginRight: 6,
                          }}
                        ></span>
                        {motor.series} {motor.displacement}
                      </li>
                    ))}
                  </ul>
                </div>
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: "none",
                    border: "1px solid #ddd",
                    maxHeight: "450px",
                    overflow: "auto",
                    width: 700,
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "#fff",
                            border: "0.5px solid", // ✅ updated from borderBottom
                            padding: "5px",
                            textAlign: "center",
                            fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                            height: "30px", // ✅ fixed height
                            minWidth: "10px", // ✅ fixed width
                            maxWidth: "120px",
                          }}
                        >
                          Motor Series
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "#fff",
                            border: "0.5px solid", // ✅ updated from borderBottom
                            padding: "5px",
                            textAlign: "center",
                            fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                            height: "30px", // ✅ fixed height
                            minWidth: "10px", // ✅ fixed width
                            maxWidth: "120px",
                          }}
                        >
                          Required <br></br>Pressure
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "#fff",
                            border: "0.5px solid", // ✅ updated from borderBottom
                            padding: "5px",
                            textAlign: "center",
                            fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                            height: "30px", // ✅ fixed height
                            minWidth: "10px", // ✅ fixed width
                            maxWidth: "120px",
                          }}
                        >
                          Required <br></br>Flow
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "#fff",
                            border: "0.5px solid", // ✅ updated from borderBottom
                            padding: "5px",
                            textAlign: "center",
                            fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                            height: "30px", // ✅ fixed height
                            minWidth: "10px", // ✅ fixed width
                            maxWidth: "120px",
                          }}
                        >
                          Overall <br></br>
                          Efficiency
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "#fff",
                            border: "0.5px solid", // ✅ updated from borderBottom
                            padding: "5px",
                            textAlign: "center",
                            fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                            height: "30px", // ✅ fixed height
                            minWidth: "10px", // ✅ fixed width
                            maxWidth: "120px",
                          }}
                        >
                          Continuous<br></br> Operation
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody
                      sx={{
                        fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                      }}
                    >
                      {selectedPoints.map((point, idx) => (
                        <TableRow
                          key={idx}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "#f4f4f4",
                            }, // Light gray for odd rows
                            "&:nth-of-type(even)": {
                              backgroundColor: "#fff",
                            }, // White for even rows
                            "&:hover": { backgroundColor: "#f1f1f1" },
                            cursor: "pointer",
                            padding: "5px",
                            fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                          }}
                        >
                          <TableCell
                            sx={{
                              // borderBottom: "1px solid #ddd",
                              textAlign: "center",
                              padding: "10px",
                              fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                              fontSize: "0.8rem",
                            }}
                          >{`${point.series} ${point.displacement}`}</TableCell>
                          <TableCell
                            sx={{
                              // borderBottom: "1px solid #ddd",
                              textAlign: "center",
                              padding: "10px",
                              fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                              fontSize: "0.8rem",
                            }}
                          >
                            {Math.round(point.pressure)}
                          </TableCell>
                          <TableCell
                            sx={{
                              // borderBottom: "1px solid #ddd",
                              textAlign: "center",
                              padding: "10px",
                              fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                              fontSize: "0.8rem",
                            }}
                          >
                            {Math.round(point.flow)}
                          </TableCell>
                          <TableCell
                            sx={{
                              // borderBottom: "1px solid #ddd",
                              textAlign: "center",
                              padding: "10px",
                              fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                              fontSize: "0.8rem",
                            }}
                          >
                            {Math.round(point.overall_efficiency)}
                          </TableCell>
                          <TableCell
                            sx={{
                              // borderBottom: "1px solid #ddd",
                              textAlign: "center",
                              padding: "10px",
                              fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                              fontSize: "0.8rem",
                            }}
                          >
                            {point.continuous_operation ? "True" : "False"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {/* <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    marginTop: "40px",
                  }}
                >
                  <button
                    onClick={downloadFromDownloadExcel}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                    style={{
                      padding: "10px 20px",
                      backgroundColor: "#e2000f",
                      backgroundColor: hover ? "#b6000f" : "#e2000f",
                      color: "#fff",
                      border: "none",
                      borderRadius: "2px",
                      cursor: "pointer",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                      fontSize: 14,
                    }}
                  >
                    Download Selected Motor Full
                    <br />
                    Performance Map
                    <DownloadIcon sx={{ fontSize: 25, margin: "2px" }} />
                  </button>
                </div> */}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EfficiencyMatrixPlot;
